import { useState, useEffect } from "react";
import { supabase, BRAND, MUTED, INK, ROW_LINE, LINE, WHITE, HOVER_BG, FAINT, pageTitle, cardFlush, text } from "./constants";
import { CandidateAvatar } from "./shared";
import { ChevronRight } from "lucide-react";
import { useIsMobile } from "./lib/responsive";

// ─── NOTIFICACIONES (03/11) ──────────────────────────────────────────────────
// La campanita del menú: lo que espera algo del cliente (avisos de
// lib/todos.js, los mismos que cuenta la burbuja roja) y, debajo, las
// próximas entrevistas de Calendly si hay. Sustituye a «Lo que toca» de la
// Home y a la burbuja de Posiciones.
const NotificationsView = ({ lang, items = [], setView, setSelectedPosition, goToCandidate, openPositionsList = [], selectedClientId = null, photoUrls = {} }) => {
  const es = lang === "es";
  const isMobile = useIsMobile();
  const [upcomingEvents, setUpcomingEvents] = useState([]);

  useEffect(() => {
    // Con cliente seleccionado se usa su token de Calendly; sin él (admin), el maestro.
    const url = selectedClientId ? `/api/calendly-events?clientId=${selectedClientId}` : "/api/calendly-events";
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const jwt = session?.access_token;
        if (!jwt) return;
        const r = await fetch(url, { headers: { Authorization: `Bearer ${jwt}` } });
        const data = await r.json();
        setUpcomingEvents(data.events || []);
      } catch {
        // sin entrevistas: no sale el bloque
      }
    })();
  }, [selectedClientId]);

  // Abrir una posición en una pestaña. La pestaña se recuerda en localStorage,
  // como hace la propia posición; `focus` = "pending" hace que el número rojo
  // de Pendientes eche dos ondas (lo lee CandidatesView una sola vez).
  const openPos = (p, tab, focus) => {
    if (tab) { try { localStorage.setItem("rutment_position_tab", JSON.stringify({ id: p.id, tab })); } catch (e) { /* sin almacenamiento */ } }
    if (focus) { try { sessionStorage.setItem("rutment_focus", JSON.stringify({ id: p.id, what: focus })); } catch (e) { /* sin almacenamiento */ } }
    setSelectedPosition(p); setView("candidates");
  };

  const rows = items.map(it => {
    const n = (it.cands || []).length;
    if (it.kind === "pending") return { ...it, onClick: () => openPos(it.p, "candidates", "pending"), sub: it.p.title,
      text: es ? `${n} ${n === 1 ? "candidato espera" : "candidatos esperan"} tu decisión` : `${n} ${n === 1 ? "candidate is" : "candidates are"} awaiting your decision` };
    if (it.kind === "mapping") return { ...it, onClick: () => openPos(it.p, "mapping"), sub: it.p.title,
      text: es ? "El mapping está listo para que lo apruebes" : "The mapping is ready for your approval" };
    if (it.kind === "eval") return { ...it, onClick: () => openPos(it.p, "evaluation"), sub: it.p.title,
      text: es ? `Falta evaluar ${n === 1 ? "1 entrevista" : `${n} entrevistas`}` : `${n} ${n === 1 ? "interview needs" : "interviews need"} scoring` };
    return { ...it, onClick: () => setView("cultureQuestionnaire"),
      sub: es ? "14 preguntas, 10-12 minutos" : "14 questions, 10-12 minutes",
      text: es ? "Haz tu Documento de cultura" : "Complete your culture statement" };
  });

  // Entrevistas de Calendly: solo las de candidatos de este cliente
  const pairs = openPositionsList.flatMap(p => (p.candidates || []).map(c => ({ c, p })));
  const matchCand = (ev) => pairs.find(({ c }) =>
    (ev.inviteeEmail && c.email && ev.inviteeEmail.toLowerCase() === c.email.toLowerCase()) ||
    (ev.inviteeName && c.name && ev.inviteeName.toLowerCase() === c.name.toLowerCase()));
  const events = upcomingEvents.filter(ev => !!matchCand(ev)).slice(0, 5);
  const fmtEvent = (iso) => {
    if (!iso) return "";
    const d = new Date(iso), today = new Date(), tomorrow = new Date(); tomorrow.setDate(today.getDate() + 1);
    const time = d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Madrid" });
    if (d.toDateString() === today.toDateString()) return `${es ? "Hoy" : "Today"} · ${time}`;
    if (d.toDateString() === tomorrow.toDateString()) return `${es ? "Mañana" : "Tomorrow"} · ${time}`;
    return d.toLocaleDateString(es ? "es-ES" : "en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "Europe/Madrid" }) + " · " + time;
  };

  const pad = isMobile ? 16 : 20;
  const avatar = (c, size = 30) => photoUrls[c.id]
    ? <img src={photoUrls[c.id]} alt={c.name} style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", border: `1.5px solid ${LINE}`, display: "block", flexShrink: 0, boxSizing: "border-box" }} />
    : <CandidateAvatar name={c.name} size={size} />;
  const rowHover = isMobile ? {} : {
    onMouseEnter: e => { e.currentTarget.style.background = HOVER_BG; },
    onMouseLeave: e => { e.currentTarget.style.background = "transparent"; },
  };
  const rowArrow = <ChevronRight size={16} strokeWidth={1.75} color={FAINT} style={{ flexShrink: 0 }} />;
  const rowStyle = (idx) => ({ display: "flex", alignItems: "center", gap: 12, padding: `11px ${pad}px`, borderTop: idx ? `1px solid ${ROW_LINE}` : "none", cursor: "pointer", minHeight: 44, boxSizing: "border-box" });
  const label = (s) => <div style={{ ...text.label, margin: "0 2px 8px" }}>{s}</div>;

  return (
    <div style={{ padding: isMobile ? 16 : 32, maxWidth: 860 }}>
      <div style={{ marginBottom: isMobile ? 20 : 32 }}>
        <h1 style={pageTitle(isMobile, 4)}>{es ? "Notificaciones" : "Notifications"}</h1>
        <p style={{ fontSize: 13, color: MUTED, margin: 0 }}>{es ? "Lo que espera algo de ti." : "What's waiting on you."}</p>
      </div>

      {label(es ? "Pendiente" : "To do")}
      <div style={{ ...cardFlush, overflow: "hidden" }}>
        {rows.length === 0 ? (
          <div style={{ padding: `16px ${pad}px`, fontSize: 13, color: MUTED, lineHeight: 1.6 }}>
            {es ? "Nada pendiente por tu parte. Te avisamos aquí cuando haya algo que decidir." : "Nothing pending on your side. We'll show it here when there's something to decide."}
          </div>
        ) : rows.map((it, idx) => (
          <div key={it.key} role="button" tabIndex={0} onClick={it.onClick} onKeyDown={e => { if (e.key === "Enter") it.onClick(); }} {...rowHover} style={rowStyle(idx)}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: INK }}>{it.text}</div>
              <div style={{ fontSize: 12, color: MUTED, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.sub}</div>
            </div>
            {it.cands && it.cands.length > 0 && (
              <div style={{ display: "flex", flexShrink: 0 }}>
                {it.cands.slice(0, 3).map((c, i) => (
                  <div key={c.id} title={c.name} style={{ marginLeft: i ? -8 : 0, borderRadius: "50%", boxShadow: `0 0 0 2px ${WHITE}`, lineHeight: 0 }}>{avatar(c, 24)}</div>
                ))}
              </div>
            )}
            {rowArrow}
          </div>
        ))}
      </div>

      {events.length > 0 && (
        <div style={{ marginTop: 24 }}>
          {label(es ? "Próximas entrevistas" : "Next interviews")}
          <div style={{ ...cardFlush, overflow: "hidden" }}>
            {events.map((ev, idx) => {
              const m = matchCand(ev);
              return (
                <div key={ev.uri} role="button" tabIndex={0} onClick={() => goToCandidate(m.c, m.p)} onKeyDown={e => { if (e.key === "Enter") goToCandidate(m.c, m.p); }} {...rowHover} style={rowStyle(idx)}>
                  {avatar(m.c)}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: INK }}>{m.c.name}</div>
                    <div style={{ fontSize: 12, color: MUTED, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{fmtEvent(ev.startTime)} · {m.p.title}</div>
                  </div>
                  {ev.location && <a href={ev.location} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: isMobile ? 13 : 12, fontWeight: 700, color: BRAND, textDecoration: "none", whiteSpace: "nowrap", ...(isMobile ? { display: "inline-flex", alignItems: "center", minHeight: 40, padding: "0 4px" } : {}) }}>{es ? "Unirse →" : "Join →"}</a>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsView;
